import { Dialog } from '@cimplico/components';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateJournals } from '../../queries/use-update-journals';
import { JournalStatusEnum } from '../../types/journal';
import plural from '../../utilities/plural';

type PostJournalsConfirmDialogProps = {
    worksheetId?: string;
    journalIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onXeroError: (error?: { name: string; message: string }) => void;
    onPostError?: (errorMessages: { [key: number]: string[] }) => void;
    onPost: () => void;
};

function PostJournalsConfirmDialog({
    worksheetId,
    journalIds,
    isOpen,
    onXeroError,
    onClose,
    onPostError,
    onPost,
}: PostJournalsConfirmDialogProps): JSX.Element {
    const postJournalsQuery = useUpdateJournals(worksheetId, journalIds.length === 1 ? journalIds[0] : undefined);

    function postJournals(): void {
        postJournalsQuery.mutate(
            journalIds.map((id) => {
                return { id, status: JournalStatusEnum.POSTED };
            }),
            {
                onSuccess: () => {
                    onPost();
                    toastNotification('success', 'Post journals success!', `${journalIds.length} posted. 0 failed to post.`);
                },
                onError: (res) => {
                    onPost();
                    const error = res.response?.data.results[0].error.fields.error;
                    const message = res.response?.data.results[0].error.fields.errorMessage;

                    if (error && message) {
                        toastNotification('error', 'Unable to post journal!', message);
                        onXeroError({ message, name: error });
                        return;
                    }

                    if (journalIds.length === 1) {
                        const errorMessage = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400)[0].error
                            .fields.errorMessage;

                        toastNotification('error', 'Unable to post journal!', errorMessage);
                    } else {
                        const errorJournals = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400).length;
                        const successJournals = res.response?.data.results.filter(
                            (r: { statusCode: number }) => r.statusCode === 200
                        ).length;

                        toastNotification('error', 'Post journals error!', `${successJournals} posted. ${errorJournals} failed to post.`);

                        const errors = res.response?.data.results.filter((r: { statusCode: number }) => r.statusCode === 400);
                        const errorMessages: { [key: number]: string[] } = {};

                        for (const err of errors) {
                            errorMessages[err.error.fields.reference] = err.error.fields.errorMessage;
                        }

                        onPostError && onPostError(errorMessages);
                    }
                },
            }
        );
    }

    return (
        <Dialog
            type="info"
            primaryAction="Post"
            secondaryAction="Cancel"
            title={`Post ${plural(journalIds.length, 'Journal', 'Journals')}`}
            description={`Do you want to post ${journalIds.length} ${plural(
                journalIds.length,
                'journal',
                'journals'
            )}? Statuses for worksheets may be affected.`}
            isOpen={isOpen}
            onClose={onClose}
            onPrimaryActionClick={postJournals}
            onSecondaryActionClick={onClose}
            isLoading={postJournalsQuery.isLoading}
        />
    );
}

export default PostJournalsConfirmDialog;
